import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { formatCompactNumber, formatCurrency } from "../../utils/formatters";

const DashboardChart = ({
  title,
  data,
  bars,
  xTickFormatter = (value) => value,
  yTickFormatter = formatCompactNumber,
  tooltipLabelFormatter = (value) => value,
  tooltipValueFormatter = (value) => [formatCurrency(value), ""],
  height = 320,
  mobileHeight = 240,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chartHeight = isMobile ? mobileHeight : height;

  const legendPayload = bars.map((bar) => ({
    id: bar.dataKey,
    type: "circle",
    value: bar.name,
    color: bar.color,
  }));

  const tooltipFormatter = (value, name) => {
    const barConfig = bars.find((b) => b.dataKey === name);
    return tooltipValueFormatter(value, barConfig?.name || name);
  };

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      {title && (
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          fontWeight="medium"
          sx={{ mb: 2 }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 10,
              right: isMobile ? 10 : 30,
              left: isMobile ? 0 : 20,
              bottom: isMobile ? 70 : 50,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="quarterly"
              tickFormatter={xTickFormatter}
              tick={{ fill: "#666", fontSize: isMobile ? 12 : 14 }}
              height={isMobile ? 40 : 30}
            />
            <YAxis
              tickFormatter={yTickFormatter}
              width={isMobile ? 60 : 80}
              tick={{ fill: "#666", fontSize: isMobile ? 11 : 13 }}
            />
            <Tooltip
              labelFormatter={tooltipLabelFormatter}
              formatter={tooltipFormatter}
              contentStyle={{
                borderRadius: 8,
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            />
            <Legend
              payload={legendPayload}
              wrapperStyle={{
                fontSize: isMobile ? 11 : 13,
                paddingTop: isMobile ? 8 : 0,
              }}
            />
            {bars.map((bar) => (
              <Bar
                key={bar.dataKey}
                dataKey={bar.dataKey}
                fill={bar.color}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default DashboardChart;
